// Iter 82.9 Sprint 2 / Phase E3.1 — Payroll Runs (list page).
//
// Operator entry point for the monthly payroll workflow.  Lists every
// run (current + historical) with status badge, period, cohort size,
// totals, exception summary.  "New Run" opens a creation dialog with
// period_month + cohort filter + variance thresholds (D10=b tunable).
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Calendar, Users, ArrowRight, Warning, CheckCircle, Info, ArrowSquareOut } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";

const STATUS_TONE = {
    Draft:             "bg-slate-100 text-slate-700",
    Calculated:        "bg-blue-100 text-blue-800",
    "Pending Approval":"bg-amber-100 text-amber-800",
    Approved:          "bg-indigo-100 text-indigo-800",
    Locked:            "bg-violet-100 text-violet-800",
    Disbursed:         "bg-emerald-100 text-emerald-800",
    Discarded:         "bg-zinc-100 text-zinc-600",
};
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
function thisMonthYM() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function PayrollRuns() {
    const { can } = useAuth();
    const canEdit = can("payroll:admin");
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [periodFilter, setPeriodFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [preflight, setPreflight] = useState(null);
    const [preflightLoading, setPreflightLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        period_month: thisMonthYM(),
        include_statuses: ["Active", "Probation"],
        include_notice_period: false,
        per_employee_mom: 10.0,
        total_payroll_mom: 5.0,
        remarks: "",
    });

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (periodFilter) params.period_month = periodFilter;
            if (statusFilter) params.status_filter = statusFilter;
            const r = await api.get("/payroll/run/list", { params });
            setItems(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load payroll runs"));
        } finally {
            setLoading(false);
        }
    }, [periodFilter, statusFilter]);

    useEffect(() => { reload(); }, [reload]);

    const buildBody = () => ({
        period_month: form.period_month,
        cohort_filter: {
            include_statuses: form.include_statuses,
            include_notice_period: form.include_notice_period,
            include_employee_ids: [], exclude_employee_ids: [],
        },
        variance_thresholds_pct: {
            per_employee_mom: Number(form.per_employee_mom),
            total_payroll_mom: Number(form.total_payroll_mom),
        },
        remarks: form.remarks,
    });

    // Iter 111.1 · Obs 10 — Payroll pre-validation. Called both on-demand
    // via "Check Readiness" and implicitly before Create.
    const runPreflight = useCallback(async () => {
        setPreflightLoading(true);
        setPreflight(null);
        try {
            const r = await api.post("/payroll/run/preflight", buildBody());
            setPreflight(r.data);
            return r.data;
        } catch (err) {
            toast.error(formatApiError(err, "Preflight check failed"));
            return null;
        } finally {
            setPreflightLoading(false);
        }
    }, [form]);

    const submit = async () => {
        // Explicit preflight if not already computed for this form.
        const pf = preflight || await runPreflight();
        if (!pf) return;
        if (!pf.can_proceed) {
            toast.error(pf.hint || "Cohort is not ready for payroll — see the readiness report.");
            return;
        }
        setCreating(true);
        try {
            const r = await api.post("/payroll/run", buildBody());
            toast.success(`Payroll run ${r.data.id} created in Draft`);
            setOpen(false);
            setPreflight(null);
            await reload();
            nav(`/admin/payroll/runs/${r.data.id}`);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to create run"));
        } finally {
            setCreating(false);
        }
    };

    // Reset preflight state when form key inputs change so operators
    // don't accidentally submit against a stale readiness report.
    useEffect(() => {
        setPreflight(null);
    }, [form.period_month, form.include_statuses, form.include_notice_period]);

    return (
        <div className="space-y-4" data-testid="payroll-runs-page">
            <PageHeader
                eyebrow="Payroll Operations"
                title="Payroll Runs"
                description={`${items.length} run${items.length === 1 ? "" : "s"} · monthly payroll lifecycle (Draft → Disbursed)`}
                actions={(
                    canEdit ? (
                        <Button onClick={() => setOpen(true)} size="sm" data-testid="run-new-btn">
                            <Plus size={14} className="mr-1" /> New Run
                        </Button>
                    ) : null
                )}
            />

            <div className="flex flex-wrap items-end gap-3 px-1">
                <div className="flex flex-col">
                    <Label className="text-xs text-slate-500 mb-1">Period</Label>
                    <Input
                        type="month"
                        value={periodFilter}
                        onChange={(e) => setPeriodFilter(e.target.value)}
                        className="h-9 w-40"
                        data-testid="run-period-filter"
                    />
                </div>
                <div className="flex flex-col">
                    <Label className="text-xs text-slate-500 mb-1">Status</Label>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="h-9 px-3 border rounded-md text-sm"
                        data-testid="run-status-filter"
                    >
                        <option value="">All</option>
                        {Object.keys(STATUS_TONE).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                {(periodFilter || statusFilter) && (
                    <Button variant="outline" size="sm" onClick={() => { setPeriodFilter(""); setStatusFilter(""); }} data-testid="run-clear-filters">
                        Clear
                    </Button>
                )}
            </div>

            <div className="border rounded-lg bg-white overflow-x-auto">
                <table className="w-full text-sm" data-testid="run-table">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
                        <tr>
                            <th className="px-3 py-2 text-left">Period</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-right">Headcount</th>
                            <th className="px-3 py-2 text-right">Gross</th>
                            <th className="px-3 py-2 text-right">Net Pay</th>
                            <th className="px-3 py-2 text-center">Exceptions</th>
                            <th className="px-3 py-2 text-left">Calc Version</th>
                            <th className="px-3 py-2 text-left">Created By</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading && (
                            <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>
                        )}
                        {!loading && items.length === 0 && (
                            <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-400" data-testid="run-empty">No payroll runs yet. Click &ldquo;New Run&rdquo; to start the cycle.</td></tr>
                        )}
                        {items.map((r) => {
                            const cnt = r.exception_count_by_severity || {};
                            const totalExc = Object.values(cnt).reduce((a, b) => a + b, 0);
                            const crit = cnt.Critical || 0;
                            const high = cnt.High || 0;
                            return (
                                <tr key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/admin/payroll/runs/${r.id}`)} data-testid={`run-row-${r.id}`}>
                                    <td className="px-3 py-2 font-mono text-xs">
                                        <div className="flex items-center gap-1.5"><Calendar size={12} className="text-slate-400" /> {r.period_month}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <Badge className={`${STATUS_TONE[r.status] || "bg-slate-100"} font-medium`} data-testid={`run-row-status-${r.id}`}>{r.status}</Badge>
                                        {r.correction_of_run_id && <Badge variant="outline" className="ml-1 text-[10px]">Correction</Badge>}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <span className="inline-flex items-center gap-1"><Users size={12} className="text-slate-400" /> {r.cohort_size || 0}</span>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono">{inr(r.totals?.gross_earnings)}</td>
                                    <td className="px-3 py-2 text-right font-mono">{inr(r.totals?.net_pay)}</td>
                                    <td className="px-3 py-2 text-center">
                                        {totalExc === 0 ? (
                                            <span className="text-emerald-600 text-xs">Clean</span>
                                        ) : (
                                            <div className="flex items-center justify-center gap-1">
                                                {crit > 0 && <Badge className="bg-rose-100 text-rose-800 text-[10px]">{crit}C</Badge>}
                                                {high > 0 && <Badge className="bg-amber-100 text-amber-800 text-[10px]">{high}H</Badge>}
                                                {totalExc > crit + high && <Badge variant="outline" className="text-[10px]">+{totalExc - crit - high}</Badge>}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-500">v{r.calc_version || 0}</td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{r.created_by_email || "—"}</td>
                                    <td className="px-3 py-2 text-right">
                                        <ArrowRight size={14} className="text-slate-300 inline-block" />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="run-create-dialog">
                    <DialogHeader>
                        <DialogTitle>New Payroll Run</DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">
                            Creates a Draft run for the selected period.
                            Cohort + variance thresholds (D10) are
                            captured as snapshots — D16 immutability.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-xs">Period Month</Label>
                            <Input
                                type="month"
                                value={form.period_month}
                                onChange={(e) => setForm({ ...form, period_month: e.target.value })}
                                data-testid="run-form-period"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Cohort — Include Statuses</Label>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {["Active", "Probation", "Notice Period"].map((s) => (
                                    <label key={s} className="flex items-center gap-1.5 text-xs cursor-pointer border rounded-md px-2 py-1">
                                        <input
                                            type="checkbox"
                                            checked={form.include_statuses.includes(s) || (s === "Notice Period" && form.include_notice_period)}
                                            onChange={(e) => {
                                                if (s === "Notice Period") {
                                                    setForm({ ...form, include_notice_period: e.target.checked });
                                                } else {
                                                    const next = e.target.checked
                                                        ? [...form.include_statuses, s]
                                                        : form.include_statuses.filter((x) => x !== s);
                                                    setForm({ ...form, include_statuses: next });
                                                }
                                            }}
                                            data-testid={`run-form-cohort-${s.replace(/\s/g, "")}`}
                                        />
                                        {s}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 pt-1">
                            <div>
                                <Label className="text-xs">Variance threshold — per employee (MoM %)</Label>
                                <Input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    value={form.per_employee_mom}
                                    onChange={(e) => setForm({ ...form, per_employee_mom: e.target.value })}
                                    data-testid="run-form-var-emp"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Variance threshold — total payroll (MoM %)</Label>
                                <Input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    value={form.total_payroll_mom}
                                    onChange={(e) => setForm({ ...form, total_payroll_mom: e.target.value })}
                                    data-testid="run-form-var-total"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Remarks (optional)</Label>
                            <Input
                                value={form.remarks}
                                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                                placeholder="e.g. Jan 2026 regular monthly payroll"
                                data-testid="run-form-remarks"
                            />
                        </div>

                        {/* Iter 111.1 · Obs 10 — Payroll Run Readiness (preflight) */}
                        <div className="border-t pt-3">
                            <div className="flex items-center justify-between mb-2">
                                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                                    Readiness Check
                                </Label>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={runPreflight}
                                    disabled={preflightLoading}
                                    data-testid="run-form-preflight-btn"
                                >
                                    {preflightLoading ? "Checking…" : (preflight ? "Re-check" : "Check Readiness")}
                                </Button>
                            </div>
                            {!preflight && !preflightLoading && (
                                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="run-form-preflight-idle">
                                    Click <b>Check Readiness</b> to validate the compliance-doc snapshot and per-employee salary assignments before creating the run.
                                </div>
                            )}
                            {preflight && (
                                <div className="space-y-2" data-testid="run-form-preflight-result">
                                    {/* Compliance line */}
                                    <div className={`rounded-md border p-2 text-xs flex items-start gap-2 ${
                                        preflight.compliance.ok
                                            ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                                            : "bg-rose-50 border-rose-200 text-rose-900"
                                    }`} data-testid="preflight-compliance">
                                        {preflight.compliance.ok
                                            ? <CheckCircle size={14} className="mt-0.5 shrink-0" />
                                            : <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />}
                                        <div>
                                            <div className="font-semibold">
                                                Compliance doc {preflight.compliance.ok ? "✓" : "missing"}
                                            </div>
                                            {preflight.compliance.ok ? (
                                                <div className="text-[10px] opacity-80 font-mono">
                                                    {preflight.compliance.doc_id} · effective {preflight.compliance.effective_from}
                                                </div>
                                            ) : (
                                                <div className="text-[10px]">{preflight.compliance.message}</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Summary counts */}
                                    <div className="grid grid-cols-3 gap-2 text-center text-xs" data-testid="preflight-counts">
                                        <div className="border rounded p-2 bg-slate-50">
                                            <div className="text-[10px] uppercase text-slate-500">Cohort</div>
                                            <div className="font-mono text-lg font-bold">{preflight.cohort_size}</div>
                                        </div>
                                        <div className="border rounded p-2 bg-emerald-50 border-emerald-200">
                                            <div className="text-[10px] uppercase text-emerald-700">Ready</div>
                                            <div className="font-mono text-lg font-bold text-emerald-900">{preflight.ready_count}</div>
                                        </div>
                                        <div className={`border rounded p-2 ${preflight.blocking_count > 0 ? "bg-rose-50 border-rose-200" : "bg-slate-50"}`}>
                                            <div className={`text-[10px] uppercase ${preflight.blocking_count > 0 ? "text-rose-700" : "text-slate-500"}`}>Blocking</div>
                                            <div className={`font-mono text-lg font-bold ${preflight.blocking_count > 0 ? "text-rose-900" : "text-slate-900"}`}>
                                                {preflight.blocking_count}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Blocking list */}
                                    {preflight.blocking_count > 0 && (
                                        <div className="border border-rose-200 rounded-md bg-rose-50/40 overflow-hidden" data-testid="preflight-blocking-list">
                                            <div className="px-2 py-1.5 border-b border-rose-200 bg-rose-100 text-[10px] uppercase font-semibold text-rose-900 flex items-center gap-1">
                                                <Warning size={12} weight="fill" />
                                                Blocking prerequisites — resolve before creating the run
                                            </div>
                                            <ul className="max-h-52 overflow-y-auto divide-y divide-rose-100 text-xs">
                                                {preflight.blocking.slice(0, 25).map((b) => (
                                                    <li key={b.employee_id} className="px-2 py-1.5 flex items-center gap-2" data-testid={`preflight-blocking-${b.employee_id}`}>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-medium truncate">
                                                                <span className="font-mono text-[10px] text-slate-500 mr-1">{b.employee_code}</span>
                                                                {b.full_name}
                                                            </div>
                                                            <div className="text-[10px] text-rose-700 truncate">{b.message}</div>
                                                        </div>
                                                        {b.fix_route && (
                                                            <button
                                                                type="button"
                                                                onClick={() => nav(b.fix_route)}
                                                                className="inline-flex items-center gap-1 text-[10px] font-medium text-rose-700 hover:text-rose-900 hover:underline shrink-0"
                                                                data-testid={`preflight-fix-${b.employee_id}`}
                                                            >
                                                                {b.fix_label || "Fix"} <ArrowSquareOut size={10} />
                                                            </button>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                            {preflight.blocking.length > 25 && (
                                                <div className="px-2 py-1 text-[10px] text-rose-700 bg-rose-100">
                                                    …and {preflight.blocking.length - 25} more. Bulk-fix via the Salary Assignment console.
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Hint */}
                                    <div className={`rounded-md p-2 text-xs flex items-start gap-2 ${
                                        preflight.can_proceed
                                            ? "bg-emerald-50 border border-emerald-200 text-emerald-900"
                                            : "bg-amber-50 border border-amber-200 text-amber-900"
                                    }`} data-testid="preflight-hint">
                                        {preflight.can_proceed
                                            ? <CheckCircle size={14} className="mt-0.5 shrink-0" />
                                            : <Info size={14} className="mt-0.5 shrink-0" />}
                                        <span>{preflight.hint}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} data-testid="run-form-cancel">Cancel</Button>
                        <Button
                            onClick={submit}
                            disabled={creating || preflightLoading || (preflight && !preflight.can_proceed)}
                            data-testid="run-form-save"
                        >
                            {creating ? "Creating…" : "Create Draft Run"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
